// app/javascript/controllers/input_assignment_columns_controller.js
//
// The "check the whole column" boxes in the input field table header.
//
// One job: keep each header box showing its column's state, and save the
// column when one is clicked. A column is saved in a single request — the
// server writes every row and returns the re-rendered table — so a column can
// never end up half applied.
//
// A column whose rows disagree shows a dash. HTML has no attribute for that,
// so the state travels in data-state and is applied here. Browsers treat a
// click on a dash as "check", which is what the column does: the first click
// checks every row, the next clears them.
import {Controller} from "stimulus";

export default class extends Controller {
    static get targets() {
        return ["header", "error"];
    }

    connect() {
        this.refresh();

        // Row boxes save themselves through inline-save, which replaces the
        // row. Watch for both the click and the replacement so the header
        // never disagrees with the rows under it.
        this.rowChanged = (event) => {
            if (this.headerTargets.includes(event.target)) return;
            this.refresh();
        };
        this.element.addEventListener("change", this.rowChanged);

        this.rowsReplaced = new MutationObserver(() => this.refresh());
        const body = this.element.querySelector("tbody");
        if (body) this.rowsReplaced.observe(body, {childList: true, subtree: true});
    }

    disconnect() {
        this.element.removeEventListener("change", this.rowChanged);
        if (this.rowsReplaced) this.rowsReplaced.disconnect();
    }

    toggle(event) {
        const header = event.target;
        const previous = header.dataset.state;
        const value = header.checked ? "1" : "0";

        this._clearError();
        this._setBusy(true);

        fetch(this.data.get("url"), {
            method: "PATCH",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "text/vnd.turbo-stream.html",
                "X-CSRF-Token": this._csrfToken()
            },
            body: this._body(header, value).toString()
        }).then(response => {
            if (response.ok) return response.text();
            throw new Error(`Column save failed: ${response.status}`);
        }).then(html => {
            if (html) Turbo.renderStreamMessage(html);
        }).catch(error => {
            console.error("Column save failed:", error);
            this._show(header, previous);
            this._showError("That column could not be saved. Please try again.");
        }).then(() => {
            this._setBusy(false);
        });
    }

    // Re-reads every header box's state from the row boxes it drives.
    refresh() {
        this.headerTargets.forEach(header => this._show(header, this._stateOf(header)));
    }

    _stateOf(header) {
        const boxes = this._rowBoxesFor(header);
        if (boxes.length === 0) return "none";

        const checked = boxes.filter(box => box.checked).length;
        if (checked === boxes.length) return "all";
        if (checked === 0) return "none";
        return "mixed";
    }

    _rowBoxesFor(header) {
        const name = header.dataset.rowName;
        if (!name) return [];

        return Array.from(this.element.querySelectorAll(`tbody input[type="checkbox"][name="${name}"]`));
    }

    _show(header, state) {
        header.dataset.state = state;
        header.checked = state === "all";
        header.indeterminate = state === "mixed";
    }

    _body(header, value) {
        const body = new URLSearchParams();
        body.set("assignable_gid", this.data.get("gid"));
        body.set("domain", header.dataset.domain);
        body.set("flag", header.dataset.flag);
        body.set("value", value);
        return body;
    }

    _setBusy(busy) {
        this.headerTargets.forEach(header => {
            header.disabled = busy;
        });
    }

    _showError(message) {
        if (!this.hasErrorTarget) return;

        this.errorTarget.textContent = message;
        this.errorTarget.hidden = false;
    }

    _clearError() {
        if (!this.hasErrorTarget) return;

        this.errorTarget.textContent = "";
        this.errorTarget.hidden = true;
    }

    _csrfToken() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.content : null;
    }
}
